import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Search, RefreshCw, Download, Loader2, History as HistoryIcon, Filter,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  "waiting", "table_assigned", "called", "seated", "completed",
  "cancelled", "no_show", "upcoming", "confirmed", "pending",
];
const SOURCE_OPTIONS = ["walk_in", "pre_booked", "phone", "online", "manager"];

const statusColor = (s) => {
  const map = {
    completed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    seated: "bg-sky-500/20 text-sky-400 border-sky-500/30",
    called: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    table_assigned: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
    waiting: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    no_show: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  };
  return map[s] || "bg-gray-500/20 text-gray-400 border-gray-500/30";
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return iso; }
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function BookingHistoryTab({ session, centers = [] }) {
  const token = session?.token;
  const isSuperAdmin = session?.is_super_admin === true;
  const isAdmin = session?.is_admin === true;
  const userCenter = session?.center || "";
  const canPickCenter = isSuperAdmin || isAdmin;

  const today = new Date().toISOString().split("T")[0];
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];

  const [filters, setFilters] = useState({
    center: canPickCenter ? "all" : userCenter,
    guest_name: "",
    mobile: "",
    token_number: "",
    booking_id: "",
    date_from: monthAgo,
    date_to: today,
    table_number: "",
    manager: "",
    status: "all",
    source: "all",
    limit: 200,
  });

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const payload = {
        token,
        center: filters.center === "all" ? null : filters.center,
        guest_name: filters.guest_name || null,
        mobile: filters.mobile || null,
        token_number: filters.token_number || null,
        booking_id: filters.booking_id || null,
        date_from: filters.date_from || null,
        date_to: filters.date_to || null,
        table_number: filters.table_number || null,
        manager: filters.manager || null,
        status: filters.status === "all" ? null : filters.status,
        source: filters.source === "all" ? null : filters.source,
        limit: filters.limit,
      };
      const res = await fetch(`${API}/api/booking-tokens/search-history`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setBookings(data.bookings || []);
    } catch (err) {
      toast.error("Failed to load history");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { fetchHistory(); /* initial */ }, []);

  const resetFilters = () => {
    setFilters({
      center: canPickCenter ? "all" : userCenter,
      guest_name: "", mobile: "", token_number: "", booking_id: "",
      date_from: monthAgo, date_to: today, table_number: "", manager: "",
      status: "all", source: "all", limit: 200,
    });
  };

  const exportCsv = () => {
    if (!bookings.length) { toast.info("Nothing to export"); return; }
    const cols = [
      "booking_id", "token_number", "center", "booking_date", "time_slot",
      "guest_name", "mobile", "contact_mobile", "adults", "children", "total_pax",
      "num_guests", "booking_source", "status", "table_number", "table_allotted",
      "celebration_type", "special_request", "menu_decided", "is_catering",
      "created_by", "created_at", "expected_arrival", "actual_arrival",
      "waiting_started", "table_assigned_at", "called_at", "seated_at",
      "completed_at", "waiting_minutes", "dining_minutes",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = [cols.join(",")].concat(
      bookings.map((b) => cols.map((c) => esc(b[c])).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `booking-history-${today}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${bookings.length} rows`);
  };

  return (
    <div className="space-y-4" data-testid="booking-history-tab">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-primary" />
              <span className="font-medium">Filters</span>
              <Badge variant="outline">{bookings.length} results</Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={resetFilters} data-testid="history-reset-btn">
                Reset
              </Button>
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="history-export-csv-btn">
                <Download className="w-4 h-4 mr-1" /> CSV
              </Button>
              <Button size="sm" onClick={fetchHistory} disabled={loading} data-testid="history-search-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Search
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {canPickCenter && (
              <div>
                <Label className="text-xs">Center</Label>
                <Select value={filters.center} onValueChange={(v) => setFilters((f) => ({ ...f, center: v }))}>
                  <SelectTrigger className="h-9" data-testid="history-center-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Centers</SelectItem>
                    {centers.map((c) => (
                      <SelectItem key={c.code || c} value={c.code || c}>
                        {c.code || c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" className="h-9" value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                data-testid="history-date-from" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" className="h-9" value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                data-testid="history-date-to" />
            </div>
            <div>
              <Label className="text-xs">Guest Name</Label>
              <Input className="h-9" placeholder="e.g. Sharma" value={filters.guest_name}
                onChange={(e) => setFilters((f) => ({ ...f, guest_name: e.target.value }))}
                data-testid="history-guest-name" />
            </div>
            <div>
              <Label className="text-xs">Mobile</Label>
              <Input className="h-9" placeholder="10-digit" value={filters.mobile}
                onChange={(e) => setFilters((f) => ({ ...f, mobile: e.target.value }))}
                data-testid="history-mobile" />
            </div>
            <div>
              <Label className="text-xs">Token #</Label>
              <Input className="h-9" placeholder="e.g. A012" value={filters.token_number}
                onChange={(e) => setFilters((f) => ({ ...f, token_number: e.target.value }))}
                data-testid="history-token-number" />
            </div>
            <div>
              <Label className="text-xs">Booking ID</Label>
              <Input className="h-9" placeholder="uuid" value={filters.booking_id}
                onChange={(e) => setFilters((f) => ({ ...f, booking_id: e.target.value }))}
                data-testid="history-booking-id" />
            </div>
            <div>
              <Label className="text-xs">Table #</Label>
              <Input className="h-9" placeholder="e.g. T03" value={filters.table_number}
                onChange={(e) => setFilters((f) => ({ ...f, table_number: e.target.value }))}
                data-testid="history-table-number" />
            </div>
            <div>
              <Label className="text-xs">Manager</Label>
              <Input className="h-9" placeholder="name" value={filters.manager}
                onChange={(e) => setFilters((f) => ({ ...f, manager: e.target.value }))}
                data-testid="history-manager" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="h-9" data-testid="history-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={filters.source} onValueChange={(v) => setFilters((f) => ({ ...f, source: v }))}>
                <SelectTrigger className="h-9" data-testid="history-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {SOURCE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Limit</Label>
              <Select value={String(filters.limit)} onValueChange={(v) => setFilters((f) => ({ ...f, limit: parseInt(v) }))}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[100, 200, 500, 1000, 2000].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <HistoryIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No history entries match the current filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[70vh]">
              <Table className="text-xs">
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Slot</TableHead>
                    <TableHead>Token</TableHead>
                    <TableHead>Center</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Adults</TableHead>
                    <TableHead>Kids</TableHead>
                    <TableHead>Pax</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Table</TableHead>
                    <TableHead>Occasion</TableHead>
                    <TableHead>Special Req.</TableHead>
                    <TableHead>Menu</TableHead>
                    <TableHead>Catering</TableHead>
                    <TableHead>Manager</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Expected</TableHead>
                    <TableHead>Arrived</TableHead>
                    <TableHead>Wait Start</TableHead>
                    <TableHead>Tbl Assigned</TableHead>
                    <TableHead>Called</TableHead>
                    <TableHead>Seated</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Wait (min)</TableHead>
                    <TableHead>Dining (min)</TableHead>
                    <TableHead>WhatsApp</TableHead>
                    <TableHead>Follow-up</TableHead>
                    <TableHead>Booking ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((b, idx) => (
                    <TableRow key={b.booking_id || idx} data-testid={`history-row-${b.booking_id || idx}`}>
                      <TableCell className="whitespace-nowrap">{b.booking_date || b.date || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap">{b.time_slot || "—"}</TableCell>
                      <TableCell className="font-mono font-semibold">{b.token_number || "—"}</TableCell>
                      <TableCell>{b.center || "—"}</TableCell>
                      <TableCell className="font-medium">{b.guest_name || "—"}</TableCell>
                      <TableCell>{b.mobile || b.contact_mobile || b.phone || "—"}</TableCell>
                      <TableCell>{b.adults ?? "—"}</TableCell>
                      <TableCell>{b.children ?? "—"}</TableCell>
                      <TableCell>{b.total_pax || b.num_guests || "—"}</TableCell>
                      <TableCell>{b.booking_source || "—"}</TableCell>
                      <TableCell>
                        <Badge className={statusColor(b.status)}>{b.status || "—"}</Badge>
                      </TableCell>
                      <TableCell>{b.table_number || b.table_allotted || "—"}</TableCell>
                      <TableCell>{b.celebration_type || "—"}</TableCell>
                      <TableCell className="max-w-[180px] truncate" title={b.special_request}>
                        {b.special_request || "—"}
                      </TableCell>
                      <TableCell>{b.menu_decided || "—"}</TableCell>
                      <TableCell>{b.is_catering ? "Yes" : "No"}</TableCell>
                      <TableCell>{b.created_by || "—"}</TableCell>
                      <TableCell>{fmtDT(b.created_at)}</TableCell>
                      <TableCell>{fmtTime(b.expected_arrival)}</TableCell>
                      <TableCell>{fmtTime(b.actual_arrival)}</TableCell>
                      <TableCell>{fmtTime(b.waiting_started)}</TableCell>
                      <TableCell>{fmtTime(b.table_assigned_at)}</TableCell>
                      <TableCell>{fmtTime(b.called_at)}</TableCell>
                      <TableCell>{fmtTime(b.seated_at)}</TableCell>
                      <TableCell>{fmtTime(b.completed_at)}</TableCell>
                      <TableCell>{b.waiting_minutes ?? "—"}</TableCell>
                      <TableCell>{b.dining_minutes ?? "—"}</TableCell>
                      <TableCell>{b.whatsapp_sent ? "Yes" : "No"}</TableCell>
                      <TableCell>{b.follow_up_required ? (b.follow_up_date || "Yes") : "—"}</TableCell>
                      <TableCell className="font-mono text-[10px]">{b.booking_id?.slice(0, 8) || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
